/**
 * @file Page through Firestore collections needed for the export. Each
 * generator yields raw Firestore docs (snake_case) — decryption and JSON
 * shaping happens later in `dataJson.ts`.
 *
 * Memory note: we yield docs in batches so the caller can stream them into
 * the zip without holding the entire family in memory.
 */

import {
  type Firestore,
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import type {
  CommentDoc,
  DigestDoc,
  EntryDoc,
  FamilyDoc,
  FamilyMembershipDoc,
  ObservationDoc,
  PersonDoc,
  ReactionDoc,
} from "../types";

const PAGE_SIZE = 100;

function familyDocRef(firestore: Firestore, familyId: string) {
  return doc(firestore, "families", familyId);
}

export async function fetchFamily(
  firestore: Firestore,
  familyId: string,
): Promise<FamilyDoc | null> {
  const snap = await getDoc(familyDocRef(firestore, familyId));
  if (!snap.exists()) return null;
  return { ...(snap.data() as FamilyDoc), id: snap.id };
}

async function* pagedSubcollection<T extends { id: string }>(
  firestore: Firestore,
  familyId: string,
  subcollection: "memberships" | "entries" | "digests",
  orderField: string,
): AsyncGenerator<T[], void, void> {
  const colRef = collection(firestore, "families", familyId, subcollection);
  let last: import("firebase/firestore").QueryDocumentSnapshot | undefined;
  while (true) {
    const q = last
      ? query(colRef, orderBy(orderField), startAfter(last), limit(PAGE_SIZE))
      : query(colRef, orderBy(orderField), limit(PAGE_SIZE));
    const snap = await getDocs(q);
    if (snap.empty) return;
    const batch = snap.docs.map((d) => ({ ...(d.data() as object), id: d.id }) as T);
    yield batch;
    if (snap.docs.length < PAGE_SIZE) return;
    last = snap.docs[snap.docs.length - 1];
  }
}

export function fetchMemberships(
  firestore: Firestore,
  familyId: string,
): AsyncGenerator<FamilyMembershipDoc[], void, void> {
  return pagedSubcollection<FamilyMembershipDoc>(firestore, familyId, "memberships", "id");
}

export function fetchEntries(
  firestore: Firestore,
  familyId: string,
): AsyncGenerator<EntryDoc[], void, void> {
  return pagedSubcollection<EntryDoc>(firestore, familyId, "entries", "created_at");
}

export function fetchDigests(
  firestore: Firestore,
  familyId: string,
): AsyncGenerator<DigestDoc[], void, void> {
  return pagedSubcollection<DigestDoc>(firestore, familyId, "digests", "created_at");
}

export async function* fetchObservations(
  firestore: Firestore,
  familyId: string,
  entryId: string,
): AsyncGenerator<ObservationDoc[], void, void> {
  const colRef = collection(firestore, "families", familyId, "entries", entryId, "observations");
  let last: import("firebase/firestore").QueryDocumentSnapshot | undefined;
  while (true) {
    const q = last
      ? query(colRef, orderBy("created_at"), startAfter(last), limit(PAGE_SIZE))
      : query(colRef, orderBy("created_at"), limit(PAGE_SIZE));
    const snap = await getDocs(q);
    if (snap.empty) return;
    yield snap.docs.map((d) => ({ ...(d.data() as ObservationDoc), id: d.id }));
    if (snap.docs.length < PAGE_SIZE) return;
    last = snap.docs[snap.docs.length - 1];
  }
}

/**
 * @what: Page through `entries/{entryId}/reactions` ordered by `created_at`,
 *   yielding raw Reaction docs (docId = ownerToken は `id` に載せる)。
 * @why: epic_18 task_459。export に各 Entry のスタンプを含めるため、observation と
 *   同じ paged 取得 pattern で 1 batch ずつ返す (大家族でもメモリ 1 ページ分に抑える)。
 * @failure: Firestore の permission denied / unavailable は `getDocs` の例外として
 *   伝播し、`useExportSession` 側で `firestore_permission_error` / `network_error`
 *   に正規化される。
 */
export async function* fetchReactions(
  firestore: Firestore,
  familyId: string,
  entryId: string,
): AsyncGenerator<ReactionDoc[], void, void> {
  const colRef = collection(firestore, "families", familyId, "entries", entryId, "reactions");
  let last: import("firebase/firestore").QueryDocumentSnapshot | undefined;
  while (true) {
    const q = last
      ? query(colRef, orderBy("created_at"), startAfter(last), limit(PAGE_SIZE))
      : query(colRef, orderBy("created_at"), limit(PAGE_SIZE));
    const snap = await getDocs(q);
    if (snap.empty) return;
    yield snap.docs.map((d) => ({ ...(d.data() as ReactionDoc), id: d.id }));
    if (snap.docs.length < PAGE_SIZE) return;
    last = snap.docs[snap.docs.length - 1];
  }
}

/**
 * @what: Page through `entries/{entryId}/comments` ordered by `created_at`,
 *   yielding raw Comment docs (docId = commentId は `id` に載せる)。
 * @why: epic_18 task_459。export に各 Entry のコメント (tombstone 含む) を含める。
 *   tombstone 済み doc も `created_at` を持つため order は壊れない (復号は runner で skip)。
 * @failure: fetchReactions と同じく `getDocs` の例外を伝播する。
 */
export async function* fetchComments(
  firestore: Firestore,
  familyId: string,
  entryId: string,
): AsyncGenerator<CommentDoc[], void, void> {
  const colRef = collection(firestore, "families", familyId, "entries", entryId, "comments");
  let last: import("firebase/firestore").QueryDocumentSnapshot | undefined;
  while (true) {
    const q = last
      ? query(colRef, orderBy("created_at"), startAfter(last), limit(PAGE_SIZE))
      : query(colRef, orderBy("created_at"), limit(PAGE_SIZE));
    const snap = await getDocs(q);
    if (snap.empty) return;
    yield snap.docs.map((d) => ({ ...(d.data() as CommentDoc), id: d.id }));
    if (snap.docs.length < PAGE_SIZE) return;
    last = snap.docs[snap.docs.length - 1];
  }
}

/**
 * @what: Fetch the `persons/{id}` documents referenced by the family
 *   memberships in chunks of up to 30 ids per query (Firestore `in` clause
 *   limit). Returns the resolved persons; missing ids are silently skipped.
 * @why: メンバー数が増えても 1 person ごとの round-trip にしないために、
 *   `where(documentId(), 'in', [...])` で 1 query あたり最大 30 件を一括取得する
 *   (Firestore の制限。Phase 1 の家族規模なら大半は 1 query で済むが、念のため
 *   chunked loop で堅牢化する)。
 * @failure: Firestore の permission denied / unavailable は `getDocs` の例外と
 *   して伝播する。`useExportSession` 側で catch して `firestore_permission_error`
 *   または `network_error` カテゴリに正規化する。
 */
export async function fetchPersons(
  firestore: Firestore,
  personIds: readonly string[],
): Promise<PersonDoc[]> {
  if (personIds.length === 0) return [];
  const CHUNK_SIZE = 30; // Firestore `in` clause max
  const out: PersonDoc[] = [];
  for (let i = 0; i < personIds.length; i += CHUNK_SIZE) {
    const chunk = personIds.slice(i, i + CHUNK_SIZE);
    const q = query(collection(firestore, "persons"), where(documentId(), "in", [...chunk]));
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      out.push({ ...(d.data() as PersonDoc), id: d.id });
    }
  }
  return out;
}

/** Internal: kept exported only because exportRunner used to call it directly.
 *  Currently unused; left for future single-doc reads. */
export async function fetchSinglePerson(
  firestore: Firestore,
  personId: string,
): Promise<PersonDoc | null> {
  const snap = await getDoc(doc(firestore, "persons", personId));
  if (!snap.exists()) return null;
  return { ...(snap.data() as PersonDoc), id: snap.id };
}
